import type {
    AlphaEyeActivePage,
    AlphaEyeWidgetModel,
    AlphaEyeWidgetModelEventBus,
    ProcessedAlphaEyeMainSection,
} from '.';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type {
    AlphaEyeNewsSubTabId,
    AlphaEyeWidgetInitialState,
    AlphaEyeWidgetRuntimeState,
    AlphaEyeWidgetState,
    MainSubTabConnectionStatusValue,
} from './AlphaEyeModel';
import { AlphaEyeModel, alphaEyeWidgetInitialStateSchema, alphaEyeWidgetStateSchema } from './AlphaEyeModel';
import type { HoldersModel } from './HoldersModel';
import type { KeyLevelsModel } from './KeyLevelsModel/KeyLevelsModel';
import type { VolumeProfileModel } from './VolumeProfileModel';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { Period } from '@thinkalpha/common/schemas/codecs.js';
import type { DictionaryBackedData, IndicatorImportRef } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { OutputColumn, OutputColumnOptions } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type {
    AlphaEyeDataListFontSize,
    AlphaEyeMainSection,
    AlphaEyeMainSectionId,
    AlphaEyePageId,
    AlphaEyeSettings,
} from '@thinkalpha/platform-ws-client/contracts/widget/alpha-eye.js';
import type { Logo } from '@thinkalpha/quant-ws-client/contracts/logo.js';
import type { SymbolRecord } from '@thinkalpha/quant-ws-client/contracts/security.js';
import { stableHash } from '@thinkalpha/ui-core/lib/hashedName';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { AlphaLensDictionaryListItem } from '@thinkalpha/ui-types/contracts/workspace';
import { optional } from 'inversify';
import { inject, injectable } from 'src/features/ioc';
import { escapeFormulaStringLiteral } from 'src/lib/escapeFormulaStringLiteral';
import { useAlphaEyeHoldersTab } from 'src/lib/featureFlags/flags/useAlphaEyeHoldersTab';
import { useAlphaEyeVolumeProfileTab } from 'src/lib/featureFlags/flags/useAlphaEyeVolumeProfileTab';
import { getCompanyLogoFromSymbol } from 'src/lib/newQuant';
import { createTable } from 'src/lib/table';
import { FilterStage, OutputStage, SourceStage } from 'src/lib/tableStages';
import type { Stage } from 'src/lib/tableStages';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { AnalystRatingsModel } from 'src/models/AnalystRatingsModel';
import type { LightweightChartModel } from 'src/models/LightweightChartModel';
import { NEW_TABLE_RESET } from 'src/models/TableModel';
import type { TableModel } from 'src/models/TableModel';
import { getNewsSummaryRollingQuery } from 'src/queries/newsSummary';
import { getSecurityMasterAccessKeyQuery, getSecurityMasterEntryQuery } from 'src/queries/securityMaster';
import { getAlphaEyeSettingsQuery, getAlphaLensDefaultWidgetQuery } from 'src/queries/userPreferences';
import type { ReactContainer } from 'src/ReactContainer';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { WidgetHostContainer } from 'src/services/WorkspaceService/Container/Container';
import type { GetInitialData, WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import { createChartFromSymbol } from 'src/store/actions/container';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';
import type { z } from 'zod';

const ALPHA_EYE_SYMBOL_PICKER_HEADER_ELEMENT = { type: 'symbolPicker', position: 30, priority: 6 } as const;

@injectable()
export class AlphaEyeWidgetModelImpl implements AlphaEyeWidgetModel {
    static readonly stateSchema: z.ZodType<AlphaEyeWidgetState> = alphaEyeWidgetStateSchema;
    static readonly initialStateSchema: z.ZodType<AlphaEyeWidgetInitialState> = alphaEyeWidgetInitialStateSchema;

    static defaultState(): AlphaEyeWidgetState {
        return { symbol: 'AAPL' };
    }

    static defaultInitialState(): AlphaEyeWidgetInitialState {
        return { initialization: 'default-symbol' };
    }

    static async fromJSON(container: ReactContainer): Promise<AlphaEyeWidgetModelImpl> {
        const getInitialData = container.get('GetInitialData') as GetInitialData<unknown>;
        const raw = await getInitialData();
        const validated = AlphaEyeWidgetModelImpl.initialStateSchema.parse(raw);
        container.bind('InitialWidgetState').toConstantValue(validated);
        return container.get('AlphaEyeWidgetModel') as AlphaEyeWidgetModelImpl;
    }

    readonly #model: AlphaEyeModel;
    readonly #disposableStack = new DisposableStack();
    readonly #settingsQuery;
    readonly #newsSummaryQuery;
    readonly #tableModelFactory: ReactBindings['TableModelFactory'];
    readonly #slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    readonly #processedMain: ReadonlySignal<ProcessedAlphaEyeMainSection[]>;
    readonly #mainHeaderTable: TableModel;
    readonly #headerConnectionStatus: ReadonlySignal<MainSubTabConnectionStatusValue>;
    readonly #securityMasterEntry: ReadonlySignal<SymbolRecord | undefined>;
    readonly #companyLogoUrl: ReadonlySignal<string | null>;
    readonly #lastPrice: ReadonlySignal<number | null>;
    readonly #symbolChangeDirections = signal<ReadonlyMap<string, number>>(new Map());
    readonly #holdersTabEnabled: ReadonlySignal<boolean>;
    readonly #volumeProfileTabEnabled: ReadonlySignal<boolean>;
    readonly #store: ReactBindings['Store'];
    readonly #hasWorkspaceContext: boolean;
    readonly headerElements: WidgetHeaderElement[];

    constructor(
        @inject('WidgetId') @optional() tabId: WidgetId | undefined,
        @inject('WidgetHostContainer') @optional() container: WidgetHostContainer | undefined,
        @inject('InitialWidgetState') initialState: unknown,
        @inject('Logger') logger: Logger,
        @inject('QueryObserverModelFactory')
        queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('TableModelFactory') tableModelFactory: ReactBindings['TableModelFactory'],
        @inject('SlugMapServiceProvider') slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('SecurityMasterService') securityMasterService: ReactBindings['SecurityMasterService'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('Store') store: ReactBindings['Store'],
        @inject('FeatureFlagService') featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('LightweightChartModel') chartModel: LightweightChartModel,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
    ) {
        const persisted = AlphaEyeWidgetModelImpl.initialStateSchema.parse(initialState);
        const hasWorkspaceService = workspaceService !== undefined;
        const hasTabId = tabId !== undefined;
        const hasContainer = container !== undefined;
        if (hasWorkspaceService && hasTabId !== hasContainer) {
            throw new Error('AlphaEye workspace model requires both WidgetId and WidgetHostContainer');
        }
        const hasWorkspaceContext = hasWorkspaceService && hasTabId && hasContainer;

        this.#tableModelFactory = tableModelFactory;
        this.#slugMapServiceProvider = slugMapServiceProvider;
        this.#store = store;
        this.#hasWorkspaceContext = hasWorkspaceContext;
        this.headerElements = hasWorkspaceContext
            ? [...COMMON_HEADER_ELEMENTS, ALPHA_EYE_SYMBOL_PICKER_HEADER_ELEMENT]
            : [
                  ...STANDALONE_COMMON_HEADER_ELEMENTS.filter(
                      (el) => el.type !== 'channelPicker' && el.type !== 'widgetName',
                  ),
                  ALPHA_EYE_SYMBOL_PICKER_HEADER_ELEMENT,
              ];

        const lastPrice = signal<number | null>(null);
        this.#lastPrice = computed(() => lastPrice.value);
        this.#settingsQuery = queryObserverModelFactory<AlphaEyeSettings, Error, AlphaEyeSettings>(
            getAlphaEyeSettingsQuery(),
        );
        this.#disposableStack.use(this.#settingsQuery);

        if (hasWorkspaceContext) {
            if (tabId === undefined || container === undefined || workspaceService === undefined) {
                throw new Error(
                    'AlphaEye workspace model requires WidgetId, WidgetHostContainer, and WorkspaceService',
                );
            }
            this.#model = this.#disposableStack.use(
                new AlphaEyeModel({
                    mode: 'workspace',
                    container,
                    tabId,
                    persisted,
                    logger,
                    loadDefaultSymbol: async () =>
                        (await queryClient.fetchQuery(getAlphaLensDefaultWidgetQuery())).symbol,
                    workspaceService,
                    settingsQuery: this.#settingsQuery.signal,
                    queryObserverModelFactory,
                    queryClient,
                    tableModelFactory,
                    slugMapServiceProvider,
                    chartModel,
                    lastPrice: this.#lastPrice,
                }),
            );
        } else {
            this.#model = this.#disposableStack.use(
                new AlphaEyeModel({
                    mode: 'standalone',
                    persisted,
                    logger,
                    loadDefaultSymbol: async () =>
                        (await queryClient.fetchQuery(getAlphaLensDefaultWidgetQuery())).symbol,
                    settingsQuery: this.#settingsQuery.signal,
                    queryObserverModelFactory,
                    queryClient,
                    tableModelFactory,
                    slugMapServiceProvider,
                    chartModel,
                    lastPrice: this.#lastPrice,
                }),
            );
        }

        // 48-hour AI summary of news for the current symbol — same wire query
        // as the legacy React `NewsSummary.tsx`. Uses the rolling-window
        // factory so each `refetchInterval` tick recomputes `from`/`to`
        // against the *current* clock; capturing `Instant.now()` in the
        // outer `computed` would freeze the 48h window at first subscribe.
        // Bound to the widget's symbol via a reactive `computed()` so symbol
        // changes re-subscribe automatically. Both the News-tab teaser and
        // the full Summary sub-tab consume this signal.
        const TWO_DAYS_MS = 172_800_000;
        this.#newsSummaryQuery = queryObserverModelFactory<string, Error, string>(
            computed(() => getNewsSummaryRollingQuery(this.#model.symbol.value, TWO_DAYS_MS)),
        );
        this.#disposableStack.use(this.#newsSummaryQuery);

        // Items returned from the ws endpoint lack `id` fields (the contract
        // mapper omits them — same as the legacy AlphaLens endpoint). Generate
        // local segment IDs for keyed rendering and stable item IDs from the
        // column payload so table creation does not churn IDs across loads.
        // Memoized by the identity of the raw `main` array — tanstack-query's
        // structural sharing reuses the array reference when the payload is
        // unchanged, so segment IDs stay stable across re-renders.
        let lastRaw: AlphaEyeMainSection[] | undefined;
        let lastProcessed: ProcessedAlphaEyeMainSection[] = [];
        this.#processedMain = computed<ProcessedAlphaEyeMainSection[]>(() => {
            const raw = this.#settingsQuery.signal.value.data?.main;
            if (!raw) return [];
            if (raw === lastRaw) return lastProcessed;
            lastRaw = raw;
            lastProcessed = raw.map((section) => ({
                id: section.id,
                segments: section.segments.map((segment) => ({
                    ...segment,
                    id: generateLocalId(),
                    items: segment.items.map((item) => ({
                        ...item,
                        id: buildAlphaEyeColumnId(item),
                    })),
                })),
            }));
            return lastProcessed;
        });

        // Header table — three universal columns (price, change, percent
        // change). Lifetime = widget lifetime. Symbol-change destroy handled
        // explicitly via the symbol signal subscription below; mirrors the
        // sub-tab destroy-on-symbol-change behavior in MainSubTab.svelte.
        this.#mainHeaderTable = this.#buildHeaderTable();
        this.#disposableStack.use(this.#mainHeaderTable);

        // Symbol-change destroy → clear stale price/change so the indicator
        // returns to 'connecting' immediately on symbol pick.
        {
            const headerTable = this.#mainHeaderTable;
            const symbolSignal = this.#model.symbol;
            let lastSymbol = symbolSignal.value;
            const unsubscribe = symbolSignal.subscribe((next) => {
                if (next === lastSymbol) return;
                lastSymbol = next;
                headerTable.destroy();
            });
            this.#disposableStack.use({ [Symbol.dispose]: () => unsubscribe() });
        }

        // Header connection-status — same `{status, progress, failureCount}`
        // shape as `mainSubTabConnectionStatus` so the reusable ConnectionStatus
        // component consumes it without any branching.
        this.#headerConnectionStatus = computed<MainSubTabConnectionStatusValue>(() => {
            const t = this.#mainHeaderTable;
            return {
                status: t.connectionStatus.value,
                progress: t.progress.value,
                failureCount: t.failureCount.value,
            };
        });

        // Drive symbolChangeDirections from the header table's update stream.
        // Mirrors AlphaLensWidgetModelImpl: a subscription (not a computed) so
        // we never write the *new* symbol with the *old* table's direction
        // during a symbol-change reset — direction only updates once real
        // table data arrives.
        this.#disposableStack.use(
            this.#mainHeaderTable.updateWithReset$.subscribe((tableUpdate) => {
                if (tableUpdate === NEW_TABLE_RESET) {
                    lastPrice.value = null;
                    this.#symbolChangeDirections.value = new Map();
                    return;
                }
                if ('header::last' in tableUpdate) {
                    const value = tableUpdate['header::last'];
                    lastPrice.value = typeof value === 'number' && Number.isFinite(value) ? value : null;
                }
                // Partial diffs may omit the column — preserve last direction.
                if (!('header::percent-change' in tableUpdate)) return;
                const percentChange = tableUpdate['header::percent-change'] as number | null | undefined;
                // Drop NaN / ±Infinity so a degenerate value can't overwrite a
                // previously-good direction with a misleading flat reading.
                if (typeof percentChange !== 'number' || !Number.isFinite(percentChange)) return;
                const direction = percentChange > 0 ? 1 : percentChange < 0 ? -1 : 0;
                const symbol = this.#model.symbol.value;
                if (!symbol) {
                    this.#symbolChangeDirections.value = new Map();
                    return;
                }
                this.#symbolChangeDirections.value = new Map([[symbol, direction]]);
            }),
        );

        // SecurityMaster entry — chains access-key → entry queries via the same
        // QueryObserverModel pattern the React `useSecurityMasterEntry` hook
        // uses (see src/features/security-master/index.ts). Access token comes
        // from Redux via the `selectAccessToken` selector pattern (masq token
        // takes precedence).
        const accessKeyQuery = queryObserverModelFactory<string, Error, string>(
            computed(() => {
                const accessToken = store.signal.value.auth.masqAccessToken ?? store.signal.value.auth.accessToken;
                return getSecurityMasterAccessKeyQuery(accessToken, queryClient, securityMasterService);
            }),
        );
        this.#disposableStack.use(accessKeyQuery);

        const entryQuery = queryObserverModelFactory<SymbolRecord, Error, SymbolRecord>(
            computed(() =>
                getSecurityMasterEntryQuery(
                    accessKeyQuery.signal.value.data,
                    securityMasterService,
                    this.#model.symbol.value,
                ),
            ),
        );
        this.#disposableStack.use(entryQuery);
        this.#securityMasterEntry = computed(() => {
            const entry = entryQuery.signal.value.data;
            const symbol = this.#model.symbol.value.trim().toUpperCase();
            if (!entry || !symbol || entry.symbol.trim().toUpperCase() !== symbol) return undefined;
            return entry;
        });

        // Company logo — same query pattern as the legacy SymbolInfoTab
        // (`useQuery({ queryKey: ['company-logo', symbol], staleTime: ONE_DAY })`).
        // Picks the first non-empty variant from the FA fallback chain — mirrors
        // AlphaLensWidgetModelImpl.getLogoUrl.
        const ONE_DAY_MS = 24 * 60 * 60 * 1000;
        const logoQuery = queryObserverModelFactory<Logo | null, Error, Logo | null>(
            computed(() => {
                const symbol = this.#model.symbol.value;
                return {
                    queryKey: ['alpha-eye-company-logo', symbol] as readonly unknown[],
                    queryFn: () => getCompanyLogoFromSymbol(symbol).catch(() => null),
                    staleTime: ONE_DAY_MS,
                    enabled: Boolean(symbol),
                };
            }),
        );
        this.#disposableStack.use(logoQuery);
        this.#companyLogoUrl = computed<string | null>(() => {
            const logos = logoQuery.signal.value.data?.logos;
            if (!logos) return null;
            return (
                logos.markVectorDark ??
                logos.markDark ??
                logos.markCompositeDark ??
                logos.logoVectorDark ??
                logos.logoDark ??
                null
            );
        });
        this.#holdersTabEnabled = featureFlagService.getSignal(useAlphaEyeHoldersTab);
        this.#volumeProfileTabEnabled = featureFlagService.getSignal(useAlphaEyeVolumeProfileTab);
    }

    /** Build the fixed three-column header table (price / change / %-change). */
    #buildHeaderTable(): TableModel {
        const slugMapServiceProvider = this.#slugMapServiceProvider;
        const symbolSignal = this.#model.symbol;

        const queryOptions = computed(() => {
            const symbol = symbolSignal.value;
            return {
                queryKey: ['alpha-eye-header', symbol],
                gcTime: 0,
                staleTime: 0,
                queryFn: async () => {
                    if (!symbol) return null;
                    const slugMapService = await slugMapServiceProvider();
                    const slugMap = slugMapService.slugMap.value;
                    const symbolIndicator = slugMap[Slugs.symbol];
                    const lastIndicator = slugMap[Slugs.last];
                    const changeIndicator = slugMap[Slugs.change];

                    const symbolRef: IndicatorImportRef = {
                        id: symbolIndicator.id,
                        alias: symbolIndicator.symbol,
                    };
                    const columns: OutputColumn[] = [
                        {
                            type: 'indicator',
                            id: 'header::last',
                            indicator: { id: lastIndicator.id },
                        },
                        {
                            type: 'indicator',
                            id: 'header::change',
                            indicator: { id: changeIndicator.id },
                        },
                        {
                            type: 'formula',
                            id: 'header::percent-change',
                            // Percent change vs. prior close. `change` is the
                            // dollar move from prior close; `last - change` is
                            // the prior close itself. We emit a 0-1 fraction;
                            // the view multiplies by 100 for display.
                            formula: 'change / (last - change)',
                            imports: [
                                { id: lastIndicator.id, alias: 'last' },
                                { id: changeIndicator.id, alias: 'change' },
                            ],
                        },
                    ];

                    let stage: Stage = SourceStage.allForKey('ticker');
                    stage = FilterStage.fromInputStage(stage, {
                        type: 'filter',
                        formula: `${symbolIndicator.symbol}=='${escapeFormulaStringLiteral(symbol)}'`,
                        imports: [symbolRef],
                    });
                    stage = OutputStage.fromInputStage(stage, {}, columns);
                    return createTable({ leafStage: stage.build() });
                },
            };
        });

        const table = this.#tableModelFactory();
        table.init({ queryOptions, initBounds: { firstRow: 0, windowSize: 1 }, initThrottle: 1000 });
        return table;
    }

    #requireActivePage(): AlphaEyeActivePage {
        const state = this.#model.state.value;
        if (state.type !== 'ready') {
            throw new Error(`AlphaEye active page requested while widget is ${state.type}`);
        }
        return state.activePage;
    }

    get symbol(): ReadonlySignal<string> {
        return this.#model.symbol;
    }

    get state(): ReadonlySignal<AlphaEyeWidgetRuntimeState> {
        return this.#model.state;
    }

    get isChartCollapsed(): ReadonlySignal<boolean> {
        return this.#model.isChartCollapsed;
    }

    get chartModel(): LightweightChartModel {
        const activePage = this.#requireActivePage();
        if (activePage.pageId !== 'main') {
            throw new Error('AlphaEye chart model requested while Main page is not active');
        }
        return activePage.chartModel;
    }

    get holdersTabEnabled(): ReadonlySignal<boolean> {
        return this.#holdersTabEnabled;
    }

    get volumeProfileTabEnabled(): ReadonlySignal<boolean> {
        return this.#volumeProfileTabEnabled;
    }

    get analystRatingsModel(): AnalystRatingsModel {
        const activePage = this.#requireActivePage();
        if (activePage.pageId !== 'analyst-recommendations') {
            throw new Error('AlphaEye analyst ratings model requested while Analyst Recommendations is not active');
        }
        return activePage.analystRatingsModel;
    }

    get holdersModel(): HoldersModel {
        const activePage = this.#requireActivePage();
        if (activePage.pageId !== 'holders') {
            throw new Error('AlphaEye holders model requested while Holders is not active');
        }
        return activePage.holdersModel;
    }

    get volumeProfileModel(): VolumeProfileModel {
        const activePage = this.#requireActivePage();
        if (activePage.pageId !== 'volume-profile') {
            throw new Error('AlphaEye volume profile model requested while Volume Profile is not active');
        }
        return activePage.volumeProfileModel;
    }

    get keyLevelsModel(): KeyLevelsModel {
        const activePage = this.#requireActivePage();
        if (activePage.pageId !== 'key-levels') {
            throw new Error('AlphaEye key levels model requested while Key Levels is not active');
        }
        return activePage.keyLevelsModel;
    }

    get keyLevelsStatsTable(): TableModel {
        const activePage = this.#requireActivePage();
        if (activePage.pageId !== 'key-levels') {
            throw new Error('AlphaEye key levels stats table requested while Key Levels is not active');
        }
        return activePage.keyLevelsStatsTable;
    }

    get currentPageId(): ReadonlySignal<AlphaEyePageId> {
        return this.#model.currentPageId;
    }

    get activeMainSubTabId(): ReadonlySignal<AlphaEyeMainSectionId> {
        return this.#model.activeMainSubTabId;
    }

    get activeNewsSubTabId(): ReadonlySignal<AlphaEyeNewsSubTabId> {
        return this.#model.activeNewsSubTabId;
    }

    get mainSubTabConnectionStatus(): ReadonlySignal<MainSubTabConnectionStatusValue> {
        return this.#model.mainSubTabConnectionStatus;
    }

    get mainSubTabTableKey(): ReadonlySignal<string> {
        return this.#model.mainSubTabTableKey;
    }

    get headerConnectionStatus(): ReadonlySignal<MainSubTabConnectionStatusValue> {
        return this.#headerConnectionStatus;
    }

    get mainHeaderTable(): TableModel {
        return this.#mainHeaderTable;
    }

    get lastPrice(): ReadonlySignal<number | null> {
        return this.#lastPrice;
    }

    get securityMasterEntry(): ReadonlySignal<SymbolRecord | undefined> {
        return this.#securityMasterEntry;
    }

    get companyLogoUrl(): ReadonlySignal<string | null> {
        return this.#companyLogoUrl;
    }

    get symbolChangeDirections(): ReadonlySignal<ReadonlyMap<string, number>> {
        return this.#symbolChangeDirections;
    }

    get dataListFontSize(): ReadonlySignal<AlphaEyeDataListFontSize> {
        return this.#model.dataListFontSize;
    }

    get goToDate(): ReadonlySignal<string | null> {
        return this.#model.goToDate;
    }

    get displayMonth(): ReadonlySignal<string | null> {
        return this.#model.displayMonth;
    }

    get chartLookback(): ReadonlySignal<Period> {
        return this.#model.chartLookback;
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#model.dirty;
    }

    get settingsQuery() {
        return this.#settingsQuery.signal;
    }

    get newsSummaryQuery() {
        return this.#newsSummaryQuery.signal;
    }

    get processedMain(): ReadonlySignal<ProcessedAlphaEyeMainSection[]> {
        return this.#processedMain;
    }

    get eventBus(): AlphaEyeWidgetModelEventBus {
        return this.#model.eventBus as AlphaEyeWidgetModelEventBus;
    }

    setSymbol(value: string): void {
        this.#model.setSymbol(value);
    }

    setIsChartCollapsed(value: boolean): void {
        this.#model.setIsChartCollapsed(value);
    }

    setCurrentPageId(value: AlphaEyePageId): void {
        this.#model.setCurrentPageId(value);
    }

    setActiveMainSubTabId(value: AlphaEyeMainSectionId): void {
        this.#model.setActiveMainSubTabId(value);
    }

    setActiveNewsSubTabId(value: AlphaEyeNewsSubTabId): void {
        this.#model.setActiveNewsSubTabId(value);
    }

    registerMainSubTabTable(id: AlphaEyeMainSectionId, table: TableModel | null): void {
        this.#model.registerMainSubTabTable(id, table);
    }

    onChartOpen(): void {
        if (!this.#hasWorkspaceContext) return;
        const symbol = this.#model.symbol.value;
        if (!symbol) return;
        const channelId = this.#model.eventBus.hasSignal('widget::channel')
            ? this.eventBus.getSignal('widget::channel').value
            : null;
        this.#store.dispatch(
            createChartFromSymbol({
                defaultSymbol: symbol,
                channelId,
            }),
        );
    }

    refetchActiveMainSubTabTable(): void {
        this.#model.refetchActiveMainSubTabTable();
    }

    refetchHeaderTable(): void {
        this.#mainHeaderTable.refetch();
    }

    setDataListFontSize(value: AlphaEyeDataListFontSize): void {
        this.#model.setDataListFontSize(value);
    }

    setGoToDate(value: string | null): void {
        this.#model.setGoToDate(value);
    }

    setChartLookback(value: Period): void {
        this.#model.setChartLookback(value);
    }

    setDisplayMonth(value: string | null): void {
        this.#model.setDisplayMonth(value);
    }

    /**
     * Build a per-sub-tab TableModel that subscribes to live indicator values
     * for the given items, filtered by the current symbol. Mirrors the legacy
     * `useAlphaLensTableByTab` semantics. The caller owns the returned model's
     * lifetime and must dispose it.
     */
    buildSubTabTable(items: AlphaLensDictionaryListItem[], tabKey: string): TableModel {
        const columns = items.map((item) => buildColumnFromItem(item));
        const slugMapServiceProvider = this.#slugMapServiceProvider;
        const symbolSignal = this.#model.symbol;

        const queryOptions = computed(() => {
            const symbol = symbolSignal.value;
            return {
                queryKey: [`alpha-eye-sub-tab-${tabKey}`, symbol],
                gcTime: 0,
                staleTime: 0,
                queryFn: async () => {
                    if (!symbol) return null;
                    const slugMapService = await slugMapServiceProvider();
                    const symbolIndicator = slugMapService.slugMap.value[Slugs.symbol];
                    const symbolRef: IndicatorImportRef = {
                        id: symbolIndicator.id,
                        alias: symbolIndicator.symbol,
                    };
                    let stage: Stage = SourceStage.allForKey('ticker');
                    stage = FilterStage.fromInputStage(stage, {
                        type: 'filter',
                        formula: `${symbolIndicator.symbol}=='${escapeFormulaStringLiteral(symbol)}'`,
                        imports: [symbolRef],
                    });
                    stage = OutputStage.fromInputStage(stage, {}, columns);
                    return createTable({ leafStage: stage.build() });
                },
            };
        });

        const table = this.#tableModelFactory();
        table.init({ queryOptions, initBounds: { firstRow: 0, windowSize: 1 }, initThrottle: 1000 });
        return table;
    }

    toJSON(): AlphaEyeWidgetState {
        const active = this.#model.keyLevelsActiveCategories.value;
        return {
            ...this.#model.toJSON(),
            keyLevels: {
                activeCategories: active ? Array.from(active) : null,
            },
        };
    }

    markClean(): void {
        this.#model.markClean();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}

function generateLocalId(): string {
    return v4().replace(/-/g, '');
}

export function buildAlphaEyeColumnId(item: DictionaryBackedData & { id?: string; options?: unknown }): string {
    const { id: _ignored, ...payload } = item;
    return `alpha-eye__${stableHash(payload, { length: 24 })}`;
}

export function buildColumnFromItem(item: DictionaryBackedData & { id: string; options?: unknown }): OutputColumn {
    const options = (item as { options?: Partial<OutputColumnOptions> }).options;
    const id = buildAlphaEyeColumnId(item);
    const { type } = item;
    switch (type) {
        case 'formula':
            return { type: 'formula', formula: item.formula, id, imports: item.imports, options };
        case 'indicator':
            return { type: 'indicator', id, indicator: item.indicator, options };
        default:
            throw new UnreachableCaseError(type);
    }
}
